import * as THREE from 'three';
import proj4 from 'proj4';
import type { GeoBounds, TileCoord } from '../../types/terrain-types.js';

/**
 * proj4.Converter is an object with forward/inverse methods, not a plain
 * function. Re-exporting it as a simple callable wrapper lets consumers treat
 * it like the original proj4(...).forward(...) object while still satisfying
 * the Converter type.
 */
function makeConverter(from: string, to: string) {
  const converter = proj4(from, to);
  return function (coords: number[]): number[] {
    return converter.forward(coords);
  };
}

export type SwissConverter = (coords: number[]) => number[];

/**
 * SwissProjection singleton
 *
 * Centralizes the proj4 coordinate-system definitions (so they are registered
 * exactly once) and exposes typed converters plus the shared tile/projection
 * helpers used by both the quantized-mesh terrain manager and the 3D tiles
 * buildings manager.
 */

const DEFAULT_LV95_ORIGIN = new THREE.Vector3(2600000, 1200000, 500);

const SWISS_BOUNDS: GeoBounds = {
  west: 5.96,
  south: 45.82,
  east: 10.49,
  north: 47.81,
};

function registerDefinitions(): void {
  // EPSG:4326 is built into proj4, but we explicitly register it so both
  // consumers reference the same definition string.
  proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');

  proj4.defs(
    'EPSG:2056',
    '+proj=somerc +lat_0=46.95240555555556 +lon_0=7.439583333333333 +k_0=1 ' +
      '+x_0=2600000 +y_0=1200000 +ellps=bessel ' +
      '+towgs84=674.374,15.056,405.346,0,0,0,0 +units=m +no_defs',
  );

  proj4.defs(
    'EPSG:3857',
    '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs',
  );

  proj4.defs('EPSG:4978', '+proj=geocent +datum=WGS84 +units=m +no_defs');
}

registerDefinitions();

const wgs84ToLv95: SwissConverter = makeConverter('EPSG:4326', 'EPSG:2056');

const lv95ToWgs84: SwissConverter = makeConverter('EPSG:2056', 'EPSG:4326');

const wgs84ToWebMercator: SwissConverter = makeConverter('EPSG:4326', 'EPSG:3857');

const wgs84ToEcef: SwissConverter = makeConverter('EPSG:4326', 'EPSG:4978');

const ecefToWgs84: SwissConverter = makeConverter('EPSG:4978', 'EPSG:4326');

/**
 * Resolve the LV95 origin from a list of point clouds, falling back to the
 * LV95 projection center. Invalid offsets are ignored with a warning so a
 * single malformed point cloud cannot silently destroy terrain placement.
 */
function resolveOrigin(
  pointClouds: readonly { pcoGeometry?: { offset?: THREE.Vector3 } }[],
): THREE.Vector3 {
  for (const pointCloud of pointClouds) {
    const offset = pointCloud?.pcoGeometry?.offset;
    if (offset && Number.isFinite(offset.x) && Number.isFinite(offset.y)) {
      return new THREE.Vector3(offset.x, offset.y, offset.z ?? 0);
    }
  }

  console.warn(
    'SwissProjection: no point cloud origin available, falling back to the LV95 projection center',
  );
  return DEFAULT_LV95_ORIGIN.clone();
}

/**
 * Read the LV95 origin from a single point cloud without mutating state.
 */
function readOrigin(pointCloud: {
  pcoGeometry?: { offset?: THREE.Vector3 };
}): THREE.Vector3 | null {
  const offset = pointCloud?.pcoGeometry?.offset;
  if (offset && Number.isFinite(offset.x) && Number.isFinite(offset.y)) {
    return new THREE.Vector3(offset.x, offset.y, offset.z ?? 0);
  }
  return null;
}

/**
 * Convert WGS84 lon/lat to tile coordinates for the TMS global-geodetic
 * profile used by the quantized-mesh terrain tiles.
 */
function lonLatToTile(lon: number, lat: number, zoom: number): TileCoord {
  const tilesX = Math.pow(2, zoom + 1);
  const tilesY = Math.pow(2, zoom);

  const x = Math.floor(((lon + 180) / 360) * tilesX);
  const y = Math.floor(((lat + 90) / 180) * tilesY);

  return { x, y };
}

/**
 * Get TMS global-geodetic tile bounds in WGS84.
 */
function tileBounds(x: number, y: number, z: number): GeoBounds {
  const tilesX = Math.pow(2, z + 1);
  const tilesY = Math.pow(2, z);

  const west = (x / tilesX) * 360 - 180;
  const east = ((x + 1) / tilesX) * 360 - 180;

  const south = (y / tilesY) * 180 - 90;
  const north = ((y + 1) / tilesY) * 180 - 90;

  return { west, south, east, north };
}

/**
 * Convert WGS84 lon/lat to Web Mercator tile coordinates.
 */
function lonLatToWebMercatorTile(lon: number, lat: number, zoom: number): TileCoord {
  const n = Math.pow(2, zoom);
  const x = Math.floor(((lon + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n);
  return { x, y };
}

/**
 * Get Web Mercator tile bounds in WGS84.
 */
function webMercatorTileBounds(x: number, y: number, zoom: number): GeoBounds {
  const n = Math.pow(2, zoom);
  const west = (x / n) * 360 - 180;
  const east = ((x + 1) / n) * 360 - 180;

  const north = (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * 180) / Math.PI;
  const south = (Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 1)) / n))) * 180) / Math.PI;

  return { west, south, east, north };
}

/**
 * Check whether two WGS84 bounding boxes intersect.
 */
function intersects(a: GeoBounds, b: GeoBounds): boolean {
  return !(a.east < b.west || a.west > b.east || a.north < b.south || a.south > b.north);
}

export const SWISS_PROJECTION = {
  wgs84ToLv95,
  lv95ToWgs84,
  wgs84ToWebMercator,
  wgs84ToEcef,
  ecefToWgs84,
  DEFAULT_LV95_ORIGIN,
  SWISS_BOUNDS,
  resolveOrigin,
  readOrigin,
  lonLatToTile,
  tileBounds,
  lonLatToWebMercatorTile,
  webMercatorTileBounds,
  intersects,
} as const;
